import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingView from "../../components/LoadingView";
import EmptyStateView from "../../components/EmptyStateView";
import { PoweredByLogo } from "../../components/PoweredByLogo";
import { useParams } from "react-router-dom";
import ContactUsFooter from "../../components/ContactUsFooter";
import { useUser } from "../../contexts/UserContext";
import { clean, cleanParticipantText } from "../../utils/utils";
import IntakeDetailInput from "./components/IntakeDetailInput";
import { useIntake } from "../../contexts/IntakeContext";
import MatterCreatedView from "./components/MatterCreatedView";
import { IntakeMessage } from "../../models/IntakeMessage";
import EvidenceUploadPanel from "./components/EvidenceUploadPanel/EvidenceUploadPanel";
import { useSocketEventByIds } from "../../hooks/useSocketEventByIds";
import { MatterType } from "../../models/MatterType";

const API_URL = process.env.REACT_APP_API_URL + "/api/v1/chat-intake";

const IntakeDetailPage: React.FC = () => {
  const { user } = useAuth();
  const { updateUser } = useUser();
  const { intakeId } = useParams<{ intakeId: string }>();
  const {
    setSelectedIntakeId,
    selectedIntake: intake,
    messages,
    setMessages,
    participants,
  } = useIntake();

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const hasSyncedUser = useRef(false);

  useSocketEventByIds(
    "intakemessages:changed",
    ["*"],
    (updatedDocument: IntakeMessage) => {
      if (updatedDocument.intake === intakeId) {
        setMessages((prev) => {
          const existingMessage = prev.find(
            (msg) => msg._id === updatedDocument._id,
          );
          if (existingMessage) {
            return prev.map((msg) =>
              msg._id === updatedDocument._id
                ? { ...msg, content: updatedDocument.content }
                : msg,
            );
          }
          return [...prev];
        });
      }
    },
  );

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!user || hasSyncedUser.current) return;
    hasSyncedUser.current = true;
    const firstName = localStorage.getItem("firstName");
    const lastName = localStorage.getItem("lastName");
    const email = localStorage.getItem("email");
    const phoneNumber = localStorage.getItem("phoneNumber");

    const userData = clean({ firstName, lastName, email, phoneNumber });
    if (Object.keys(userData).length) {
      updateUser(user.uid, null, userData);
      ["firstName", "lastName", "phoneNumber"].forEach(localStorage.removeItem);
    }
  }, [user, updateUser]);

  useEffect(() => {
    if (intakeId && user) {
      setSelectedIntakeId(intakeId);
    }
  }, [intakeId, user]);

  const sendMessage = () => {
    if (input.trim()) {
      getCompletion(input);
      setInput("");
      setAttachments([]);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const getCompletion = async (message: string) => {
    setIsSending(true);
    const userMessage: IntakeMessage = {
      _id: `${Date.now()}-user`,
      role: "user",
      content: message,
    };

    const aiMessageId = `${Date.now()}-ai`;
    setMessages((prev) => [
      ...prev,
      userMessage,
      { _id: aiMessageId, role: "ai", content: "" },
    ]);

    try {
      const formData = new FormData();
      formData.append("intakeId", intakeId!);
      formData.append("message", message);
      if (attachments.length > 0) formData.append("files", attachments[0]);

      const token = await user?.getIdToken();
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok || !response.body) {
        throw new Error("Stream response failed");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let finalContent = "";
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        if (
          buffer.includes(".") ||
          buffer.includes("?") ||
          buffer.length > 50
        ) {
          finalContent += buffer;
          setMessages((prev) =>
            prev.map((msg) =>
              msg._id === aiMessageId ? { ...msg, content: finalContent } : msg,
            ),
          );
          buffer = "";
        }
      }

      if (buffer.length > 0) {
        finalContent += buffer;
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === aiMessageId ? { ...msg, content: finalContent } : msg,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching AI response:", error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen">
      <div className="w-full max-w-screen-2xl">
        <div className="flex flex-row gap-4 p-4">
          {/* LEFT Panel */}
          <div className="flex flex-col w-full p-4 bg-white border rounded-lg shadow-md dark:bg-zinc-900 dark:border-zinc-800">
            {intake?.status === "processing" && (
              <LoadingView title="Analyzing your answers" />
            )}
            {intake?.status === "completed" && (
              <>
                <h2 className="mb-4 text-xl font-bold">
                  Upload Supporting Documents
                </h2>
                <p className="mb-4 text-gray-600 dark:text-gray-300">
                  If you have any documents, emails, screenshots, or contracts
                  related to this matter, please upload them here.
                </p>
                <EvidenceUploadPanel
                  intakeId={intakeId}
                  files={intake.files ?? []}
                />
              </>
            )}
            {intake?.status === "matter_created" && (
              <MatterCreatedView intake={intake} />
            )}
            {intake?.status === "initiated" && (
              <>
                <div
                  ref={chatContainerRef}
                  className="flex flex-col w-full h-[500px] overflow-y-auto space-y-2 p-2"
                >
                  {messages.length === 0 && (
                    <EmptyStateView
                      title="This is Mattery, our AI assistant"
                      message="Please provide a brief overview of the matter to begin."
                    />
                  )}
                  {messages.map((msg) => (
                    <div
                      key={msg._id}
                      className={`p-3 w-fit max-w-[75%] rounded-lg ${
                        msg.role === "user"
                          ? "self-end bg-blue-500 text-white"
                          : "self-start bg-gray-200 dark:bg-gray-700 text-black dark:text-white"
                      }`}
                    >
                      {cleanParticipantText(msg.content, participants ?? [])}
                      {msg.file}
                    </div>
                  ))}
                </div>
                <div className="pt-4">
                  <IntakeDetailInput
                    inputRef={inputRef}
                    input={input}
                    setInput={setInput}
                    sendMessage={sendMessage}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    handleKeyDown={function (
                      event: React.KeyboardEvent<HTMLTextAreaElement>,
                    ): void {
                      throw new Error("Function not implemented.");
                    }} // handleKeyDown={handleKeyDown}
                  />
                </div>
              </>
            )}
          </div>
          {/* RIGHT Panel */}
          <div className="flex flex-col w-full p-4 bg-white border rounded-lg shadow-md dark:bg-zinc-900 dark:border-zinc-800">
            <h2 className="mb-4 text-xl font-bold">Matter Details</h2>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  {(intake?.matterType as MatterType)?.name}
                  {intake?.summary}
                </div>
                <EvidenceUploadPanel
                  intakeId={intakeId}
                  files={intake?.files ?? []}
                />
              </div>
            </div>
          </div>
        </div>

        <ContactUsFooter />
        <PoweredByLogo className="hidden h-8 sm:block" />
      </div>
    </div>
  );
};

export default IntakeDetailPage;
